import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import { GOOGLE_INTEGRATION_SCOPES, MICROSOFT_INTEGRATION_SCOPES } from "@omina/core";

/**
 * The better-auth browser client.
 *
 * No `baseURL`: the SPA is served by the same Worker that mounts better-auth
 * at /api/auth/*, so the default (the current origin) is correct and there is
 * nothing to configure per environment. In dev, Vite proxies /api to
 * `wrangler dev` — same story from the browser's point of view.
 *
 * The organization plugin mirrors apps/api/src/auth.ts. Every user gets an
 * organization at sign-up (see the databaseHooks there), so the client never
 * has to handle an org-less session; it is here so `authClient.organization.*`
 * exists when the product grows past one person per company.
 */
export const authClient = createAuthClient({
  plugins: [organizationClient()],
});

/**
 * Google is the only way in, and that is architecture, not a gap: better-auth
 * hashes passwords with scrypt, and Workers' free plan gives a request 10ms of
 * CPU. See AGENTS.md.
 *
 * `integrations` is what separates "connect my calendar and mail" from "skip
 * for now". The provider in apps/api/src/auth.ts requests identity only, and
 * better-auth *appends* a call's `scopes` to it — so this one flag is the
 * difference between a consent screen with five scopes on it and one with
 * three, without either being a second trip through Google.
 *
 * Callers that mean "connect" must pass it. A missing flag does not fail: it
 * signs her in perfectly well and then reads nothing, forever, which is the
 * silent-empty-screen failure the Google card exists to make legible.
 */
export function signInWithGoogle(
  callbackURL = "/",
  opts: { integrations?: boolean } = {},
) {
  return authClient.signIn.social({
    provider: "google",
    /**
     * Absolute, against the origin of the page she is standing on — not the
     * relative path it used to be.
     *
     * better-auth resolves a relative `callbackURL` against its own `baseURL`,
     * and `baseURL` is the origin the *callback* arrived on. Google calls the
     * Worker directly at :8787, so it always resolved there.
     *
     * In production that is invisible and correct: the SPA and the API are one
     * Worker on one origin. In development they are not — Vite serves the app
     * on :5173 and `wrangler dev` answers on :8787 — so approving Google threw
     * her out of the dev server and onto the Worker's copy of `apps/app/dist`,
     * which is whatever was last built. She came back from consent into a
     * stale app on a different port, which looks exactly like the product
     * breaking at the moment she agreed to something.
     *
     * `location.origin` is the same string in production, so this costs
     * nothing there — and it is checked against the same `trustedOrigins`
     * allowlist the Worker uses either way, so it cannot become an open
     * redirect.
     */
    callbackURL: new URL(callbackURL, window.location.origin).toString(),
    /**
     * Where Google's refusals land.
     *
     * Without this, better-auth sends them to its own `/api/auth/error` page —
     * outside the product, unstyled, and carrying a bare machine code. A
     * founder who declined the consent screen, or whose account is not on the
     * tester list while the Google review is pending, therefore leaves Omina
     * entirely at the exact moment she needs a sentence explaining what to do.
     *
     * The callback appends `?error=` (and sometimes `error_description`) to
     * whatever this points at, so `/login` reads them and says it in words.
     */
    errorCallbackURL: new URL("/login", window.location.origin).toString(),
    ...(opts.integrations ? { scopes: [...GOOGLE_INTEGRATION_SCOPES] } : {}),
  });
}

/**
 * The same handoff, for a founder whose calendar and mail live at Microsoft.
 *
 * Deliberately a twin of `signInWithGoogle` rather than one function taking a
 * provider: the two differ in the scopes they carry and in nothing else, and a
 * shared function would have hidden that the scope list is the entire point of
 * the `integrations` flag on both sides.
 *
 * Same `errorCallbackURL` for the same reason — Entra refuses more often than
 * Google does, because a work tenant can block an app its admin has not
 * approved, and that refusal has to arrive somewhere it can be explained.
 */
export function signInWithMicrosoft(
  callbackURL = "/",
  opts: { integrations?: boolean } = {},
) {
  return authClient.signIn.social({
    provider: "microsoft",
    callbackURL: new URL(callbackURL, window.location.origin).toString(),
    errorCallbackURL: new URL("/login", window.location.origin).toString(),
    ...(opts.integrations ? { scopes: [...MICROSOFT_INTEGRATION_SCOPES] } : {}),
  });
}

export function signOut() {
  return authClient.signOut();
}
